import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import MediaInfoFactory from 'mediainfo.js';
import { Router } from '@angular/router';
import { VideoLibraryService } from '../video-library.service';
import { HaloToastService,EToastType } from 'src/app/core/halo-toast/halo-toast.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { VideoInfoRaw, VideoLibraryExt } from '../video-library.model';
import { HaloLoaderService } from 'src/app/core/halo-loader/halo-loader.service';
import { RootScopeDeclare } from 'src/app/core/rootscope-declare';
import { environment } from 'src/environments/environment';
import { FacilityObject } from 'src/app/core/services/facility.model';
@Component({
  selector: 'app-upload-video-library',
  templateUrl: './upload-video-library.component.html',
  styleUrls: ['./upload-video-library.component.scss']
})
export class UploadVideoLibraryComponent implements OnInit,OnDestroy {
  currentRoute:string;
  minDuration = 60; // second. 1min
  maxDuration = 6000; // second.100min
  minResolution = 720; // 720P.
  maxResolution = 1080; // 1080P.
  minFrameRate = 24; // 24FPS.
  maxFrameRate = 30; // 30FPS.
  maxFileSize = 5368709120; // 5GB => multiply 1024*3 to transform byte.
  fileFormat = 'video/mp4';
  videoInfo = '';
  hasSe4 = false;
  hasSe3 = false;
  customLoaderErrorMsg:string;
  customLoaderRedirectButtonLabel:string;
  customLoaderRedirectButtonUrl:string;
  videoLibraryList: VideoLibraryExt;
  hasFullPermission = false;
  cloudBasePath = environment.cloudBasePath;
  currentFacility: FacilityObject;
  @Input() isChildPage: boolean;
  private sub = new Subscription(); 
  constructor(
    private _router: Router, 
    private _vdoLibraryService:VideoLibraryService,
    private toast: HaloToastService,
    private _facilityService: FacilityService,
    private _loader: HaloLoaderService,
    private commonUtil: CommonUtil) { 
  }
  ngOnInit(): void {
    this.currentFacility = this._facilityService.selectedFacility();
    this.hasFullPermission = this.currentFacility.summary.userRights.customLibrary === 'F' || this.currentFacility.summary.isLoggedInManagerAdmin === 'true';
    this.loadVideoLibraryList();

    const facilityChanges = this._facilityService.selectedFacilitySource.subscribe(val => {
      this.currentFacility = val;
      if(!this.isChildPage) this.checkPermission();
      this.hasFullPermission = this.currentFacility.summary.userRights.customLibrary === 'F' || this.currentFacility.summary.isLoggedInManagerAdmin === 'true';
      this.checkConsoleType();
    })    
    this.sub.add(facilityChanges);
    if(!this.isChildPage) this.checkPermission();
    this.checkConsoleType();
  }
  
  async loadVideoLibraryList() {
    this.videoLibraryList = await this._vdoLibraryService.getVideoLibraryList(
      this._facilityService.selectedFacilityId(),
      '1',
      '10',
      'status',
      'asc'
      );
    const shouldRedirectBack = !this.videoLibraryList?.allowUpload || this.videoLibraryList?.countExceeded || this.videoLibraryList?.minutesExceeded;

    if (shouldRedirectBack) {
      this._router.navigateByUrl('/videoLibrary');
    }
  }

  async onFileDropped(files: any) {
    if(!this.hasFullPermission) return;
    if(files.length>5){
      this.toast.creatToast('Maximum 5 videos at a time.', EToastType.ERROR);
      return;
    }
    this._loader.startProcess();
    const videoList: VideoInfoRaw[] = [];
    const filesForUpload: VideoInfoRaw[] = [];
    files = [...files];
    if(files.length>0){
      for (const file of files) {
        const video = await this.validateFile(file);
        if(video.attributes?.validationErrors?.length === 0) {
          filesForUpload.push(video);
        }
        videoList.push(video);
      }
      if(filesForUpload.length > 0) {
        await this.uploadFiles(filesForUpload);
      }
      this._vdoLibraryService.setUploadingVideoList(videoList);
      this._loader.endProcess();
      if(!this.isChildPage) this._router.navigateByUrl('/videoLibrary');
    }
  }

  private async checkPermission() {
    if(this.currentFacility?.summary) {
      this._loader.startProcess();
      const isEligibleLFODFacilityRight = this.currentFacility.summary.facilityRights.customLibrary === "true";
      const hasViewPermission = this.currentFacility.summary.userRights.customLibrary !== "H";
      const isEligibleConsole =  this._facilityService.isEligibleLFODPremiumConsole(this.currentFacility);
      this._loader.endProcess();
      if(!isEligibleConsole || !isEligibleLFODFacilityRight || !hasViewPermission){
        this._router.navigateByUrl('/videoLibrary');
      }
    }
  }
  
  private async validateFile(file: File): Promise<VideoInfoRaw> {
    const validationErrors = [] as string[];
    let fileDuration;
    // Check format. MP4
    if (file.type !== 'video/mp4' && file.type !== 'video/quicktime') {
      validationErrors.push('UPLOAD.VIDEO.UPLOAD.INVALID.FORMAT.ERROR');
    } else {
      // Check fileSize. Max 5GB
      if (Number(file.size) > this.maxFileSize) {
        validationErrors.push('UPLOAD.VIDEO.UPLOAD.FILE.SIZE.ERROR');
      }

      let tmp = [file] as any[];
      tmp = tmp.reduce( (a,b,i) =>  {return a[i]=b,a},{});
      const data = await this.getMediaInfo(tmp);
      const mediaData = JSON.parse(data.replace(/(?:\r\n|\r|\n)/g, ''));
      const mediaInfo = mediaData.media.track[1];
      fileDuration = Number(mediaInfo.Duration);
      const frameRate = Number(mediaInfo.FrameRate);
      const videoHeight = Number(mediaInfo.Height);

      // Check duration. 5-100 min
      if (fileDuration < this.minDuration || fileDuration > this.maxDuration) {
        validationErrors.push('UPLOAD.VIDEO.UPLOAD.LENGTH.ERROR');
      }

      // Check resolution. 720p-1080p
      if (videoHeight < this.minResolution || videoHeight > this.maxResolution) {
        validationErrors.push('UPLOAD.VIDEO.UPLOAD.RESOLUTION.ERROR');
      }

      // Check frameRate. 24-30 FPS
      if (!(Math.ceil(frameRate) == this.minFrameRate || Math.ceil(frameRate) == this.maxFrameRate)) {
        validationErrors.push('UPLOAD.VIDEO.UPLOAD.FRAMERATE.ERROR');
      }
    }
    const response: VideoInfoRaw = {
      attributes: {
        facilityId: this.currentFacility.facilityId,
        validationErrors: validationErrors,
        muxAsset: {
          data: {
            attributes: {
              title: file.name,
              duration: fileDuration,
            }
          },
        }
      },
      statusCode: 1
    };
    if(validationErrors.length === 0) {
      response.attributes.uploadProgress = 0;
      response.attributes.file = file;
    }

    return response;
  }

  private async uploadFiles(videoList: VideoInfoRaw[]) {
    try {
      const fileNames = videoList.map(v => v.attributes?.file?.name);
      const duration = videoList.map(v => v.attributes?.muxAsset?.data.attributes.duration);
      const durationSum = duration.reduce((accumulator, currentValue) => {
        if (currentValue && accumulator != undefined) {
          return accumulator + currentValue;
        }
        return accumulator;
      },0);
      const payload = {fileNames:fileNames,facilityId:Number(this._facilityService.selectedFacilityId()), totalDuration: [durationSum], browserTime: this._vdoLibraryService.getBrowserTime()};
      const strapiInfos = await this._vdoLibraryService.getUploadVideoUrl(payload) as any;
      for (const video of videoList) {
        if(!video.attributes?.file) continue;      
        const strapiInfo = strapiInfos.find((s:any) => s.fileName === video.attributes?.file?.name);
        if(strapiInfo && video.attributes?.muxAsset?.data) {
          video.attributes.muxAsset.data.id = strapiInfo.strapiMuxAssetId;
          this._vdoLibraryService.uploadVideoLibrary(video, strapiInfo.uploadURL);
        }
      }
    } catch (error) {
      console.error('API error');
    }    
  }

  // Convert BytesToSize
  bytesToSize(bytes: number) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes == 0) return 'n/a';
    const floorNumber: number = Math.floor(Math.floor(Math.log(bytes) / Math.log(1024)));
    const i = parseInt(floorNumber.toString());
    if (i == 0) return bytes + ' ' + sizes[i];
    return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
  }

  // File Reader Promise Javascript function to get video duration.
  getVideoDuration = (file: any) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const media = new Audio(reader.result as any);

        media.onloadedmetadata = () => resolve(media.duration);
      };
      reader.readAsDataURL(file);
      reader.onerror = (error) => reject(error);
    });


  getMetadata(mediainfo: any, fileinput: any): Promise<string> {
    return new Promise<string>((resolve) => {
      const file = fileinput[0];
      if (!file) {
        return resolve("Can't get media information")
      }

      const getSize = () => file.size
      const readChunk: any = (chunkSize: any, offset: any) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (event: ProgressEvent<FileReader>) => {
            if (event.target) {
              if (event.target.error) {
                reject(event.target.error);
              } else {
                resolve(new Uint8Array(event.target.result as ArrayBuffer));
              }
            }
          }
          reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize))
        })
      const p = <Promise<any>>mediainfo.analyzeData(getSize, readChunk)
      p.then((result: string) => {
        resolve(result);
      }).catch(() =>
        resolve("Can't get media information")
      )
    })
  }

  getMediaInfo(file: any): Promise<any> {
    return new Promise((resolve, reject) => {
      MediaInfoFactory({ format: 'JSON' }, (mediainfo: any) => {
        resolve(this.getMetadata(mediainfo, file));
      });
    })
  }

  navigateToBack(): void {
    this._router.navigate(['/videoLibrary']);
  }

  checkConsoleType() {
    if(this.currentFacility && this.currentFacility.consoleEquipments && this.currentFacility.consoleEquipments.length > 0) {
      const se4Consoles = ['hal', 'neo_se4', 'everest', 'falco'];
      const se3Consoles = ['superman'];
      this.hasSe4 = !!this.currentFacility.consoleEquipments.find(item => se4Consoles.includes(item.consoleType));
      this.hasSe3 = !!this.currentFacility.consoleEquipments.find(item => se3Consoles.includes(item.consoleType));    
    }
  }

  
  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

}
